/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ModelDetailsObject } from 'src/modules/modelDetails/components/ModelDetailsListTable';

export type TrainingJobTableCommandBarProps = TableCommandBarProps<ModelDetailsObject> & {
    onStartTrainingJob: () => void;
    onCancelTrainingJob: () => void;
    isStartTrainingJobDisabled?: boolean;
    isCancelTrainingJobDisabled: boolean;
    startTrainingJobDisabledMsg?: string;
    cancelTrainingJobDisabledMsg?: string;
};

const messages = defineMessages({
    startTrainingJob: {
        id: 'modelTraining.trainingJobTableCommandBar.startTrainingJob',
        defaultMessage: 'Start a training job'
    },
    searchPlaceholder: {
        id: 'modelTraining.trainingJobTableCommandBar.searchPlaceholder',
        defaultMessage: 'Search'
    },
    cancel: {
        id: 'modelTraining.trainingJobTableCommandBar.cancel',
        defaultMessage: 'Cancel'
    }
});

export const TrainingJobTableCommandBar = observer((props: TrainingJobTableCommandBarProps) => {
    const {
        query = '',
        onQueryChange,
        onStartTrainingJob,
        onCancelTrainingJob,
        isStartTrainingJobDisabled = false,
        isCancelTrainingJobDisabled,
        startTrainingJobDisabledMsg = '',
        cancelTrainingJobDisabledMsg = ''
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const projectIdForTelemetry = rootStore.currentCluProjectStore?.id ?? rootStore.currentCustomTextProjectStore?.id;

    const handleStartTraining = () => {
        logButtonClick(serviceName, 'Training', 'StartTrainingJob', null, projectIdForTelemetry);
        onStartTrainingJob();
    };
    const items: ICommandBarItemProps[] = [
        {
            key: 'startTrainingJob',
            onRender: (item: ICommandBarItemProps) =>
                canWrite ? (
                    <LuisTooltip hidden={!isStartTrainingJobDisabled} content={startTrainingJobDisabledMsg}>
                        <CommandBarButton
                            role={'menuitem'}
                            onClick={handleStartTraining}
                            iconProps={{ iconName: 'Add' }}
                            text={localize(messages.startTrainingJob)}
                            name={localize(messages.startTrainingJob)}
                            data-automation-id="startTrainingJobButton"
                            disabled={item.disabled || isStartTrainingJobDisabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    </LuisTooltip>
                ) : (
                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        data-automation-id="startTrainingJobButtonLanguageToolTip"
                        hidden={item.disabled || isStartTrainingJobDisabled}
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                disabled
                                role={'menuitem'}
                                onClick={onStartTrainingJob}
                                iconProps={{ iconName: 'Add' }}
                                text={localize(messages.startTrainingJob)}
                                name={localize(messages.startTrainingJob)}
                                data-automation-id="startTrainingJobButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                )
        },
        {
            key: 'cancelTrainingJob',
            onRender: (item: ICommandBarItemProps) =>
                canWrite ? (
                    <LuisTooltip hidden={!isCancelTrainingJobDisabled} content={cancelTrainingJobDisabledMsg}>
                        <CommandBarButton
                            role={'menuitem'}
                            onClick={onCancelTrainingJob}
                            iconProps={{ iconName: 'Cancel' }}
                            text={localize(messages.cancel)}
                            name={localize(messages.cancel)}
                            data-automation-id="cancelTrainingJobButton"
                            disabled={item.disabled || isCancelTrainingJobDisabled}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                        />
                    </LuisTooltip>
                ) : (
                    <RBACTooltip
                        displayedUserRole={displayedUserRole}
                        data-automation-id="cancelTrainingJobButtonLanguageToolTip"
                        hidden={item.disabled || isCancelTrainingJobDisabled}
                        componentSurroundedByToolTip={
                            <CommandBarButton
                                disabled
                                role={'menuitem'}
                                onClick={onCancelTrainingJob}
                                iconProps={{ iconName: 'Cancel' }}
                                text={localize(messages.cancel)}
                                name={localize(messages.cancel)}
                                data-automation-id="cancelTrainingJobButton"
                                styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            />
                        }
                    />
                )
        }
    ];

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedSearchBox
                    ariaLabel={localize(messages.searchPlaceholder)}
                    role="menuitem"
                    size={24}
                    value={query}
                    placeholder={localize(messages.searchPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                    styles={{ root: { alignSelf: 'center' } }}
                />
            )
        }
    ];

    return <LuisCommandBar items={items} query={query} onQueryChange={onQueryChange} farItems={farItems} />;
});
